import type { ReactNode } from "react";
import type { OperationsIntakeItemDto } from "@/api/endpoints";
import { fmtETDate } from "@/lib/format";
import { ATTENTION_CLASS, ATTENTION_LABEL, INTAKE_SOURCE_LABEL, workTypeLabel } from "../operationsLabels";
import { StartDateCell } from "./StartRiskCells";

/** Where the intake came from, for the Reference column: "Bullhorn 161261", "Beeline 108462", or the FBO request subtype. */
export function intakeReference(item: OperationsIntakeItemDto): string {
  const system = item.sourceSystem?.replace(/^Procom/i, "") ?? null;
  if (item.sourceReference) return system ? `${system} ${item.sourceReference}` : item.sourceReference;
  return system ?? INTAKE_SOURCE_LABEL[item.source];
}

/** A row with a pre-verification opens that request; any other row opens the intake itself. `via` tells the detail page where "back" is. */
export function intakeHref(item: OperationsIntakeItemDto): string {
  return item.preOnboardingRequestId
    ? `/requests/${encodeURIComponent(item.preOnboardingRequestId)}?via=operations`
    : `/intake/${item.source}/${encodeURIComponent(item.id)}?via=operations`;
}

interface Props {
  items: OperationsIntakeItemDto[];
  /** What the empty table body shows (the caller knows whether filters are on). */
  empty: ReactNode;
  /** Without the Type and Status columns: the desk views, where the rows are one owner's (2026-10-27). */
  compact?: boolean;
}

/** The intake rows of the intake drawer and of the owner desk views: one table, two column sets. */
export function IntakeItemsTable({ items, empty, compact = false }: Props) {
  const cols = compact ? 9 : 11;
  const open = (item: OperationsIntakeItemDto) => window.open(intakeHref(item), "_blank", "noopener");
  return (
    <div className="tbl-wrap">
      <table className={`tbl ops-items ops-intake-items${compact ? " compact" : ""}`}>
        <thead>
          <tr>
            <th className="fit">Source</th>
            {!compact && <th className="fit">Type</th>}
            <th>Worker</th>
            <th>Client</th>
            <th>Pending with</th>
            {!compact && <th className="fit">Status</th>}
            <th>Attention</th>
            <th className="numeric">Waiting (bd)</th>
            <th className="fit" title="While the work order awaits its request: the version's contract start date with the lead in business days; the pill marks it At risk or Breached.">Start</th>
            <th className="fit">Started</th>
            <th>Reference</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={cols}>{empty}</td>
            </tr>
          )}
          {items.map((i) => (
            <tr key={`${i.source}-${i.id}`} className="linkrow" onClick={() => open(i)}>
              <td className="fit"><span className={`ops-pill src-${i.source}`}>{INTAKE_SOURCE_LABEL[i.source]}</span></td>
              {!compact && <td className="fit">{workTypeLabel(i.workType)}</td>}
              <td className="worker">
                <a
                  className="row-link"
                  href={intakeHref(i)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={i.preOnboardingRequestId ? `Open pre-verification ${i.preOnboardingRequestId.slice(0, 8)} in a new tab` : `Open intake ${i.id.slice(0, 8)} in a new tab`}
                >
                  <b>{i.worker ?? "—"}</b>
                  <span className="material-symbols-rounded" aria-hidden="true">open_in_new</span>
                </a>
              </td>
              <td className="client">{i.client ?? <span className="muted">—</span>}</td>
              <td>{i.pendingWith ?? <span className="muted">Unassigned</span>}</td>
              {!compact && (
                <td className="fit">
                  {i.journeyState === "awaitingRequest" ? <span className="ops-pill stage" title="The work order exists; nobody has raised the request yet.">WO · awaiting request</span> : i.status ? <span className="ops-pill stage">{i.status}</span> : <span className="muted">—</span>}
                </td>
              )}
              <td>
                <span className="ops-pills">
                  <span className={`ops-pill att-${ATTENTION_CLASS[i.attention]}`}><i className="dot" />{ATTENTION_LABEL[i.attention]}</span>
                  {i.reason && <span className="muted">{i.reason}</span>}
                </span>
              </td>
              <td className="numeric">{i.waitingBd}</td>
              <StartDateCell risk={i.startRisk} leadBd={i.startLeadBd} startDate={i.startDate} />
              <td className="fit nowrap">{fmtETDate(i.startedUtc)}</td>
              <td className="nowrap">{intakeReference(i)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
